import React, { useMemo } from 'react';
import { useRouteMatch } from 'react-router-dom';
import PropTypes from 'prop-types';

import {
  CryptoFormatter,
  IntlManager,
  NotificationManager,
  SiteConfigManager,
  useSettings,
} from '@atlas/core';
import { Button, DefaultPopup, Label } from '@atlas/ui';

import { urlSeparator } from 'common/constants';

const withSpecificCopyTextPayments = ['coinspaid', 'coinspaidV2'];

const parseQuery = queryString => {
  const query = {};
  const pairs = queryString.substring(queryString.indexOf('?') + 1).split('&');
  pairs.forEach(element => {
    const pair = element.split('=');
    query[decodeURIComponent(pair[0])] = decodeURIComponent(pair[1] || '');
  });
  return query;
};

const BitcoinPopup = ({ data = {}, id }) => {
  const { lang } = useSettings();
  const languageCode = useMemo(() => {
    const currentLang = SiteConfigManager.getConfig('languages')?.find(
      language => language.code === lang,
    );
    return currentLang?.hreflang || currentLang?.alpha3Code;
  }, [lang]);
  const { url } = useRouteMatch();
  const [isCopied, setIsCopied] = React.useState(false);
  const backUrlArray = url.split(urlSeparator);
  backUrlArray.pop();

  const { redirectUrl } = data;
  const { address, amount, currencyTo, exchangeRate, tag } = parseQuery(redirectUrl);
  const paymentMethodName = redirectUrl.split('?')[0];

  const isSpecificCopyText = React.useMemo(() => {
    if (!paymentMethodName) return false;
    return withSpecificCopyTextPayments.includes(paymentMethodName);
  }, [paymentMethodName]);

  const copy = React.useCallback(
    e => {
      e.preventDefault();
      const text = address;
      const inp = document.createElement('textarea');
      document.body.appendChild(inp);
      inp.value = text;
      inp.select();
      document.execCommand('copy', false);
      document.body.removeChild(inp);
      setIsCopied(true);
    },
    [data, setIsCopied],
  );

  const textParams = React.useMemo(() => ({ currency: currencyTo }), [currencyTo]);

  const buttonLabel = React.useMemo(() => {
    return isCopied ? 'copied' : isSpecificCopyText ? 'copy_address' : 'copy_pix';
  }, [isSpecificCopyText, isCopied]);

  const handleClose = React.useCallback(() => {
    NotificationManager.removeNotification(id);
  }, [id]);

  const currencyToFormatter = useMemo(() => {
    return new CryptoFormatter(languageCode, currencyTo);
  }, [languageCode, currencyTo]);

  return (
    <DefaultPopup
      className="crypto-modal"
      headerTitle={currencyTo}
      headerTitlePosition="left"
      onClose={handleClose}
    >
      <div className="crypto-modal__content">
        <Label
          className="popup__text"
          message="crypto_deposit_message_currency"
          params={textParams}
        />

        <div className="crypto-modal__inner">
          <div className="crypto-modal-item">
            <div className="crypto-modal-item__name">
              <Label className="crypto-modal-item__name" message="withdrawal_rate" />:
            </div>

            <div className="crypto-modal-item__value">
              {`${currencyToFormatter.format(10 ** 9)} = ${IntlManager.formatMoney(exchangeRate * 100)}`}
            </div>
          </div>

          <div className="crypto-modal-item">
            <div className="crypto-modal-item__name">
              <Label message="amount" />:
            </div>
            <div className="crypto-modal-item__value">
              {currencyToFormatter.format(amount * 10 ** 9)}
            </div>
          </div>

          {tag && tag !== 'null' && (
            <div className="crypto-modal-item">
              <div className="crypto-modal-item__name">
                <Label message="tag" />:
              </div>
              <div className="crypto-modal-item__value">{tag}</div>
            </div>
          )}
        </div>

        <div className="crypto-modal__code">{address}</div>

        <Button
          className="primary big block"
          disabled={isCopied}
          label={buttonLabel}
          onClick={copy}
        />
      </div>
    </DefaultPopup>
  );
};

BitcoinPopup.propTypes = { data: PropTypes.object };

export default BitcoinPopup;
